import type { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { LoadState } from './useHojeLoader'

interface HojeSectionProps<T> {
  title: string
  description?: string
  icon: ComponentType<{ className?: string }>
  /** Visão completa do bloco. */
  href: string
  linkLabel?: string
  state: LoadState<T>
  children: (data: T) => ReactNode
}

/** Moldura comum aos blocos da tela Hoje: título, link para a visão completa e estados. */
export function HojeSection<T>({ title, description, icon: Icon, href, linkLabel = 'Ver tudo', state, children }: HojeSectionProps<T>) {
  if (state.status === 'forbidden') return null
  const headingId = `hoje-${href.replace(/[^a-z0-9]+/gi, '-')}`
  return (
    <Card aria-labelledby={headingId} role="region">
      <CardHeader>
        <CardTitle id={headingId} className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
        <CardAction>
          <Button asChild variant="ghost" size="sm">
            <Link to={href} aria-label={`${linkLabel}: ${title}`}>
              {linkLabel} <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {state.status === 'loading' && (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-5" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        )}
        {state.status === 'error' && <p role="alert" className="text-sm text-destructive">{state.message}</p>}
        {state.status === 'ok' && children(state.data)}
      </CardContent>
    </Card>
  )
}

export function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>
}
